import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Quote, ArrowRight, TrendingUp } from "lucide-react";

// Phase 25.2d — Auto-rotating case study widget for the homepage.
// Pulls from /api/case-studies (public, no auth), shows up to 3 at a time,
// rotates every 8 sec. Renders nothing if no published case studies exist —
// graceful no-op until the first WON deal is converted via /app/admin/sales.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROTATION_MS = 8000;

export default function CaseStudyRotator() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${BACKEND_URL}/api/case-studies?limit=6`).catch(() => null),
      axios.get(`${BACKEND_URL}/api/public/social-proof`).catch(() => null),
    ]).then(([cs, sp]) => {
      if (cancelled) return;
      if (cs) setItems(cs.data?.items || []);
      if (sp) setStats(sp.data);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    timerRef.current = setInterval(() => {
      setActiveIdx(i => (i + 1) % items.length);
    }, ROTATION_MS);
    return () => clearInterval(timerRef.current);
  }, [items.length, paused]);

  if (items.length === 0) {
    // No published case studies yet — show only the social-proof counter
    // (still better than rendering nothing once homepage has any traffic).
    if (!stats || (stats.operators < 2 && stats.countries < 1)) return null;
    return <SocialProofCounter stats={stats} />;
  }

  const cs = items[activeIdx];
  const firstMetric = Object.values(cs.dara_metrics || {}).find(m => m && m.value);

  return (
    <section
      className="border-b border-zinc-900 py-12 sm:py-16 bg-gradient-to-b from-black via-[#0a0a0a] to-black"
      data-testid="case-study-rotator"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00E676]">
            // OPERATORS RUNNING ON DARA
          </div>
          <Link
            to="/case-studies"
            data-testid="rotator-all-link"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1"
          >
            ALL STORIES <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <Link
          to={`/case-studies/${cs.slug}`}
          key={cs.slug}
          data-testid={`rotator-card-${cs.slug}`}
          className="block border border-zinc-900 hover:border-[#FFB000]/50 bg-zinc-950/40 hover:bg-zinc-950 transition-colors group p-6 sm:p-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 items-center">
            {/* Quote + customer */}
            <div className="lg:col-span-2 min-w-0">
              <Quote className="w-5 h-5 text-[#FFB000] mb-4" />
              <p className="font-heading font-black text-xl sm:text-2xl lg:text-3xl tracking-tight text-white leading-[1.2]">
                {cs.quote_body
                  ? `"${(cs.quote_body.length > 200 ? cs.quote_body.slice(0, 200) + "…" : cs.quote_body)}"`
                  : cs.headline}
              </p>
              <div className="flex items-center gap-3 mt-5 flex-wrap">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {cs.quote_attribution || `${cs.customer_name}`}
                </div>
                {cs.industry && (
                  <span className="px-2 py-0.5 border border-zinc-800 bg-black font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                    {cs.industry}
                  </span>
                )}
              </div>
            </div>

            {/* Headline metric */}
            {firstMetric && (
              <div className="lg:col-span-1 border-l border-zinc-900 pl-6 lg:pl-8">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  {firstMetric.label}
                </div>
                <div className="font-heading font-black text-4xl lg:text-5xl tracking-tighter text-[#FFB000]">
                  {firstMetric.value}
                </div>
                {firstMetric.change_pct && (
                  <div className="font-mono text-[10px] text-[#00E676] mt-2 inline-flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> {firstMetric.change_pct}
                  </div>
                )}
                <div className="mt-5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-[#FFB000]">
                  Read full story <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            )}
          </div>
        </Link>

        {/* Pagination dots — only when 2+ studies */}
        {items.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {items.map((it, i) => (
              <button
                key={it.slug}
                onClick={() => { setActiveIdx(i); setPaused(true); }}
                data-testid={`rotator-dot-${i}`}
                aria-label={`Show ${it.customer_name}`}
                className={`h-1.5 transition-all ${
                  i === activeIdx
                    ? "w-8 bg-[#FFB000]"
                    : "w-1.5 bg-zinc-700 hover:bg-zinc-500"
                }`}
              />
            ))}
          </div>
        )}

        {/* Live social-proof counter — auto-pulls real numbers (Phase 25.2e) */}
        {stats && (stats.operators >= 1 || stats.countries >= 1) && (
          <SocialProofStrip stats={stats} />
        )}
      </div>
    </section>
  );
}

function SocialProofStrip({ stats }) {
  const cells = [
    stats.operators >= 1 && {
      label: "Operators",
      value: stats.operators >= 100 ? `${Math.floor(stats.operators / 10) * 10}+` : stats.operators,
      sub: "running on DARA",
    },
    stats.countries >= 1 && {
      label: "Countries",
      value: stats.countries,
      sub: "tracked in last 30 days",
    },
    stats.case_studies >= 1 && {
      label: "Case Studies",
      value: stats.case_studies,
      sub: "live customer stories",
    },
  ].filter(Boolean);
  if (cells.length === 0) return null;
  return (
    <div className="grid grid-cols-3 gap-px bg-zinc-900 mt-8 border border-zinc-900" data-testid="social-proof-strip">
      {cells.map((c, i) => (
        <div key={i} className="bg-black p-4 sm:p-6 text-center" data-testid={`social-proof-cell-${i}`}>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600 mb-1">
            {c.label}
          </div>
          <div className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#FFB000]">
            {c.value}
          </div>
          <div className="font-mono text-[9px] text-zinc-500 mt-1">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function SocialProofCounter({ stats }) {
  return (
    <section className="border-b border-zinc-900 py-12 sm:py-16 bg-gradient-to-b from-black via-[#0a0a0a] to-black" data-testid="social-proof-counter-only">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00E676] mb-6 text-center">
          // OPERATORS RUNNING ON DARA
        </div>
        <SocialProofStrip stats={stats} />
      </div>
    </section>
  );
}
